import { useCustomToast } from '@/hooks/use-custom-toast';
import { memo, useCallback, useMemo } from 'react';
import { useDeleteGoogleReviewIntegration } from '../../../api/delete-integration-google-review.api';
import { queryClient } from '@/lib/react-query';
import { GET_LIST_INTEGRATION_GOOGLE_REVIEW_QUERY_KEY } from '../../../api/get-list-integration-google-review.api';
import { axiosExtractErrorMessage } from '@/lib/axios';
import { useChannelModalRootActionsStore } from '@/features/channels/_shared/components/channel-modal-root/channel-modal-root.store';
import ModalWithConfirmation from '@/components/_ui/modal-with-confirmation/modal-with-confirmation';

const TOAST_MESSAGES_DELETE_GOOGLE_REVIEW = {
  success: 'The channel integration has been successfully deleted!',
  error: 'Failed to delete google review integration!',
};

export type TDeleteLocationGoogleReviewModalType = 'DeleteLocationGoogleReviewModal';
export interface IDeleteLocationGoogleReviewModalProps {
  organizationChannelId: string;
}

const DeleteLocationGoogleReviewModal = memo(({ organizationChannelId }: IDeleteLocationGoogleReviewModalProps) => {
  const showToast = useCustomToast();
  const channelModalRootActions = useChannelModalRootActionsStore();

  const isOpenDeleteLocationGoogleReviewModal = useMemo(() => {
    return channelModalRootActions.checkIsOpenModal('DeleteLocationGoogleReviewModal');
  }, [channelModalRootActions]);

  const mutationDeleteIntegrationGoogleReview = useDeleteGoogleReviewIntegration({
    mutationConfig: {
      onError: (error) => {
        const errorMessage = axiosExtractErrorMessage(error) || TOAST_MESSAGES_DELETE_GOOGLE_REVIEW.error;
        showToast({ type: 'error', message: errorMessage });
      },
      onSuccess: () => {
        Promise.all([queryClient.invalidateQueries({ queryKey: [GET_LIST_INTEGRATION_GOOGLE_REVIEW_QUERY_KEY] })]);
        channelModalRootActions.closeModal('DeleteLocationGoogleReviewModal');
        showToast({ type: 'success', message: TOAST_MESSAGES_DELETE_GOOGLE_REVIEW.success });
      },
    },
  });

  const handleDelete = useCallback(() => {
    mutationDeleteIntegrationGoogleReview.mutate({
      organizationChannelId: organizationChannelId || '',
    });
  }, [mutationDeleteIntegrationGoogleReview, organizationChannelId]);

  const handleClose = useCallback(() => {
    channelModalRootActions.closeModal('DeleteLocationGoogleReviewModal');
  }, [channelModalRootActions]);

  return (
    <ModalWithConfirmation
      isOpen={isOpenDeleteLocationGoogleReviewModal}
      onClose={handleClose}
      onDelete={handleDelete}
      isLoading={mutationDeleteIntegrationGoogleReview.isPending}
      title="Delete Google Review Integration"
      description="Do you want to delete this integration?"
      bannerTitle="Deleting this channel will permanently delete all related data, including chat history and analytics."
      confirmText="delete my channel"
    />
  );
});

DeleteLocationGoogleReviewModal.displayName = 'DeleteLocationGoogleReviewModal';

export default DeleteLocationGoogleReviewModal;
